import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, Eye, EyeOff, GripVertical, ImagePlus, LayoutTemplate, MapPin, Palette, Plus, RotateCcw, Save, SlidersHorizontal, Sparkles, Trash2, Upload, X } from 'lucide-react';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { api, type MenuItem, type Site, type Zone } from '../api/client';
import { NeedsSite } from '../app/Layout';
import { useSession, useSite } from '../app/session';
import { Select } from '../components/Select';
import { ErrorBox, Loading, PageHeader, Toggle } from '../components/ui';
import { useRooms } from './GuestsPage';
import { Glyph } from './TvMenuPage';

/** Mirrors HomeDesign in packages/contracts (admin.ts) and HOME_DEFAULTS in the TV app (apps/lg-procentric/js/app.js). */
interface Design {
  layout: 'BOTTOM_ROW' | 'LEFT_MENU' | 'GRID' | 'CARDS';
  tileStyle: 'ICON_LABEL' | 'ICON' | 'PHOTO';
  tileSize: 'S' | 'M' | 'L';
  tileShape: 'ROUND' | 'ROUNDED' | 'SQUARE';
  welcome: 'LEFT' | 'CENTER' | 'HIDDEN';
  logo: 'LEFT' | 'CENTER';
  showPromo: boolean;
  showHint: boolean;
  shade: number;
  tileColor: string | null;
  textColor: string;
  firstFocus: string | null;
  tileImageAssetIds: Record<string, string>;
  buttonOrder: string[];
  hiddenKeys: string[];
}
const DEFAULTS: Design = {
  layout: 'BOTTOM_ROW',
  tileStyle: 'ICON_LABEL',
  tileSize: 'M',
  tileShape: 'ROUNDED',
  welcome: 'LEFT',
  logo: 'LEFT',
  showPromo: true,
  showHint: true,
  shade: 100,
  tileColor: null,
  textColor: '#FFFFFF',
  firstFocus: null,
  tileImageAssetIds: {},
  buttonOrder: [],
  hiddenKeys: [],
};

/** Ready-made starting points; everything stays editable after picking one. */
const PRESETS: { name: string; hint: string; design: Partial<Design> }[] = [
  { name: 'Classic', hint: 'Buttons along the bottom', design: { ...DEFAULTS } },
  { name: 'Side menu', hint: 'A list on the left, the photo free', design: { layout: 'LEFT_MENU', tileStyle: 'ICON_LABEL', tileShape: 'ROUNDED', welcome: 'LEFT', shade: 70 } },
  { name: 'Tiles', hint: 'Two rows of buttons, centred', design: { layout: 'GRID', tileStyle: 'ICON_LABEL', welcome: 'CENTER', logo: 'CENTER', showPromo: false, shade: 85 } },
  { name: 'Picture cards', hint: 'Large cards with your photos', design: { layout: 'CARDS', tileStyle: 'PHOTO', tileShape: 'ROUNDED', welcome: 'LEFT', showPromo: false, shade: 80 } },
];

const LAYOUTS: { v: Design['layout']; label: string; hint: string; art: ReactNode }[] = [
  { v: 'BOTTOM_ROW', label: 'Bottom row', hint: 'Buttons in a row under the welcome', art: <LayoutArt kind="row" /> },
  { v: 'LEFT_MENU', label: 'Left menu', hint: 'A list on the left (up/down)', art: <LayoutArt kind="list" /> },
  { v: 'GRID', label: 'Grid', hint: 'Two rows of buttons', art: <LayoutArt kind="grid" /> },
  { v: 'CARDS', label: 'Cards', hint: 'Few large picture cards', art: <LayoutArt kind="cards" /> },
];

function LayoutArt({ kind }: { kind: 'row' | 'list' | 'grid' | 'cards' }) {
  const r = (x: number, y: number, w: number, h: number, k: number) => <rect key={k} x={x} y={y} width={w} height={h} rx={1.5} fill="currentColor" opacity={k === 0 ? 1 : 0.45} />;
  const parts =
    kind === 'row'
      ? [0, 1, 2, 3, 4].map((i) => r(6 + i * 11, 26, 9, 8, i))
      : kind === 'list'
        ? [0, 1, 2, 3].map((i) => r(4, 6 + i * 8, 18, 6, i))
        : kind === 'grid'
          ? [0, 1, 2, 3, 4, 5, 6, 7].map((i) => r(9 + (i % 4) * 11, 18 + Math.floor(i / 4) * 10, 9, 8, i))
          : [0, 1, 2].map((i) => r(8 + i * 17, 14, 14, 20, i));
  return (
    <svg viewBox="0 0 64 40" width={64} height={40} style={{ borderRadius: 6, background: '#2b2350', color: '#fff' }}>
      {kind !== 'list' && <rect x={6} y={6} width={22} height={4} rx={1} fill="#fff" opacity={0.7} />}
      {kind === 'list' && <rect x={28} y={14} width={22} height={4} rx={1} fill="#fff" opacity={0.7} />}
      {parts}
    </svg>
  );
}

/** Same numbers as homeGeometry() in the TV app (1280 × 720 screen). */
function geometry(d: Design) {
  const sz = d.tileSize === 'S' ? 0 : d.tileSize === 'L' ? 2 : 1;
  if (d.layout === 'LEFT_MENU') {
    const h = [54, 64, 76][sz]!;
    return { kind: 'list' as const, w: 360, h, gap: 10, cols: 1, visible: Math.floor(470 / (h + 10)), x: 48, y: 150 };
  }
  if (d.layout === 'GRID') {
    const gw = [170, 200, 236][sz]!,
      gh = [104, 124, 148][sz]!,
      cols = [5, 4, 4][sz]!;
    return { kind: 'grid' as const, w: gw, h: gh, gap: 14, cols, visible: cols * 2, x: Math.round((1280 - cols * gw - (cols - 1) * 14) / 2), y: 380 - (sz === 2 ? 40 : 0) };
  }
  if (d.layout === 'CARDS') {
    const cw = [220, 260, 300][sz]!,
      ch = [250, 290, 320][sz]!,
      n = [5, 4, 3][sz]!;
    return { kind: 'row' as const, w: cw, h: ch, gap: 18, cols: n, visible: n, x: Math.round((1280 - n * cw - (n - 1) * 18) / 2), y: 690 - ch - 50 };
  }
  const tw = [130, 160, 190][sz]!,
    th = [116, 140, 166][sz]!,
    vis = [8, 7, 6][sz]!;
  return { kind: 'row' as const, w: tw, h: th, gap: 12, cols: vis, visible: vis, x: Math.round((1280 - vis * tw - (vis - 1) * 12) / 2), y: 600 - th - 14 };
}

const label = (m: MenuItem) => m.label || m.defaultLabel;
const isHome = (m: MenuItem) => m.enabled && m.available && (m.placement === 'HOME' || m.placement === 'BOTH');

/** Home-capable buttons in the design's order (keys it names first, the rest in TV Menu order), as homeItems() on the TV. */
export function orderedItems(all: MenuItem[], d: Pick<Design, 'buttonOrder'>) {
  const rank = (m: MenuItem, i: number) => {
    const r = d.buttonOrder.indexOf(m.key);
    return r < 0 ? 1000 + i : r;
  };
  return all
    .filter(isHome)
    .map((m, i) => ({ m, r: rank(m, i) }))
    .sort((a, b) => a.r - b.r)
    .map((x) => x.m);
}

/** Named design for some buildings, floors or rooms (Branding.homeDesigns). */
interface Variant {
  id: string;
  name: string;
  home: Partial<Design>;
  backgroundAssetId?: string | null;
  welcomeHeadline?: string | null;
  welcomeSubline?: string | null;
  zoneIds: string[];
  roomIds: string[];
}

/** What the preview takes from Welcome & Branding (the form's values while editing there). */
export interface BrandLook {
  siteName: string;
  timezone: string;
  logoUrl: string | null;
  backgroundUrl: string | null;
  headline: string;
  subline: string;
  accent: string;
  hour12: boolean;
  showRoom: boolean;
  showClock: boolean;
  showDate: boolean;
}
export const fullDesign = (h: unknown): Design => ({ ...DEFAULTS, ...((h as Partial<Design> | undefined) ?? {}) });

export function HomeDesignerPage() {
  const site = useSite();
  return <NeedsSite>{site ? <Designer key={site.id} site={site} /> : <PageHeader title="Home screen designer" />}</NeedsSite>;
}

function Designer({ site }: { site: Site }) {
  const { can } = useSession();
  const qc = useQueryClient();
  const editable = can('ADMIN');
  const assets = useQuery({ queryKey: ['assets'], queryFn: () => api.get<{ items: { id: string; url: string }[] }>('/assets') });
  const menu = useQuery({ queryKey: ['menu', site.id], queryFn: () => api.get<{ items: MenuItem[] }>(`/sites/${site.id}/menu`) });
  const zones = useQuery({ queryKey: ['zones', site.id], queryFn: () => api.get<{ items: Zone[] }>(`/sites/${site.id}/zones`) });
  const rooms = useRooms(site.id);
  const b = site.branding as Record<string, unknown>;
  const [defDraft, setDefDraft] = useState<Design | null>(null);
  const [varDraft, setVarDraft] = useState<Variant[] | null>(null);
  const [sel, setSel] = useState('default');
  const [drag, setDrag] = useState<number | null>(null);
  const [uploaded, setUploaded] = useState<Record<string, string>>({});
  const variants = varDraft ?? ((b.homeDesigns as Variant[] | undefined) ?? []);
  const variant = sel === 'default' ? null : (variants.find((v) => v.id === sel) ?? null);
  const def = defDraft ?? fullDesign(b.home);
  const d = variant ? fullDesign(variant.home) : def;
  const setVariant = (patch: Partial<Variant>) => setVarDraft(variants.map((v) => (v.id === variant?.id ? { ...v, ...patch } : v)));
  const set = (patch: Partial<Design>) => (variant ? setVariant({ home: { ...d, ...patch } }) : setDefDraft({ ...d, ...patch }));
  const all = menu.data?.items ?? [];
  const homeItems = orderedItems(all, d);
  const shownItems = homeItems.filter((m) => !d.hiddenKeys.includes(m.key));
  const urlOf = (id: string | null | undefined) => (id ? (uploaded[id] ?? assets.data?.items.find((a) => a.id === id)?.url ?? null) : null);
  const remember = (a: { id: string; url: string }) => setUploaded((u) => ({ ...u, [a.id]: a.url }));
  const accent = /^#[0-9A-F]{6}$/i.test(String(b.accentColor ?? '')) ? String(b.accentColor) : '#6E4BFF';
  const look: BrandLook = {
    siteName: site.name,
    timezone: site.timezone,
    logoUrl: urlOf(b.hotelLogoAssetId as string),
    backgroundUrl: urlOf(variant?.backgroundAssetId || (b.homeBackgroundAssetId as string)),
    headline: variant?.welcomeHeadline || String(b.welcomeHeadline ?? 'Welcome'),
    subline: variant?.welcomeSubline || String(b.welcomeSubline ?? `We wish you a pleasant stay at ${site.name}.`),
    accent,
    hour12: !!b.hour12,
    showRoom: b.showRoom !== false,
    showClock: b.showClock !== false,
    showDate: b.showDate !== false,
  };
  const keys = new Set(all.map((m) => m.key));
  const clean = (h: Partial<Design>) => ({ ...h, tileImageAssetIds: Object.fromEntries(Object.entries(h.tileImageAssetIds ?? {}).filter(([k]) => keys.has(k))) });

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/sites/${site.id}`, {
        branding: {
          ...(defDraft ? { home: clean(defDraft) } : {}),
          ...(varDraft
            ? { homeDesigns: varDraft.map((v) => ({ ...v, name: v.name.trim(), home: clean(v.home), welcomeHeadline: v.welcomeHeadline || null, welcomeSubline: v.welcomeSubline || null })) }
            : {}),
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['sites'] });
      setDefDraft(null);
      setVarDraft(null);
    },
  });
  const dirty = !!defDraft || !!varDraft;
  const badName = variants.some((v) => !v.name.trim());
  const moveTo = (from: number, to: number) => {
    if (to < 0 || to >= homeItems.length || from === to) return;
    const next = [...homeItems];
    const [m] = next.splice(from, 1);
    next.splice(to, 0, m!);
    set({ buttonOrder: next.map((x) => x.key) });
  };
  const addVariant = () => {
    const id = `d${Date.now().toString(36)}`;
    setVarDraft([...variants, { id, name: `Design ${variants.length + 2}`, home: { ...def }, zoneIds: [], roomIds: [] }]);
    setSel(id);
  };
  // The design a zone / room already has, so the list can say so.
  const takenBy = (kind: 'zoneIds' | 'roomIds', id: string) => variants.find((v) => v.id !== variant?.id && v[kind].includes(id))?.name;
  const zoneList = zones.data?.items ?? [];
  const zoneLabel = (z: Zone) => {
    const names = [z.name];
    let parentId = z.parentId;
    while (parentId && names.length < 5) {
      const p = zoneList.find((x) => x.id === parentId);
      if (!p) break;
      names.unshift(p.name);
      parentId = p.parentId;
    }
    return names.join(' › ');
  };
  if (menu.isLoading) return <Loading />;

  return (
    <>
      <PageHeader
        title="Home screen designer"
        sub="Design the welcome screen your guests see first. One design shows in every room; add other designs for some buildings, floors or rooms."
        actions={
          <>
            <Link to="/experience/welcome" className="btn">
              <ArrowLeft size={16} /> Welcome &amp; Branding
            </Link>
            {editable && (
              <>
                {save.isSuccess && !dirty && <span className="badge ok">Published</span>}
                <button
                  className="btn"
                  disabled={!dirty}
                  onClick={() => {
                    setDefDraft(null);
                    setVarDraft(null);
                    setSel('default');
                  }}
                  title="Undo the changes you haven't saved"
                >
                  <RotateCcw size={16} /> Reset
                </button>
                <button className="btn primary" disabled={!dirty || save.isPending || badName} onClick={() => save.mutate()}>
                  <Save size={16} /> Save &amp; publish
                </button>
              </>
            )}
          </>
        }
      />
      <ErrorBox error={save.error} />
      <div className="card designer-picker">
        <span className="designer-picker-label">Design</span>
        <div className="tabs">
          <button type="button" className={`tab${sel === 'default' ? ' active' : ''}`} onClick={() => setSel('default')}>
            All rooms (default)
          </button>
          {variants.map((v) => (
            <button key={v.id} type="button" className={`tab${sel === v.id ? ' active' : ''}`} onClick={() => setSel(v.id)}>
              {v.name || 'Unnamed'}
            </button>
          ))}
        </div>
        {editable && variants.length < 50 && (
          <button type="button" className="btn btn-soft" onClick={addVariant}>
            <Plus size={16} /> New design for some rooms
          </button>
        )}
      </div>

      <div className="brand-layout designer-layout">
        <div className="card">
          {variant ? (
            <Block icon={<MapPin size={17} />} title="Where this design shows" sub="A room chosen here wins, then its floor or area, then its building. Rooms not chosen in any design show the default.">
              <Field label="Design name">
                <input value={variant.name} maxLength={80} disabled={!editable} onChange={(e) => setVariant({ name: e.target.value })} />
              </Field>
              <Field label="Buildings, floors and areas">
                <MultiPick
                  selected={variant.zoneIds}
                  disabled={!editable}
                  options={zoneList.map((z) => ({ id: z.id, label: zoneLabel(z), note: takenBy('zoneIds', z.id) }))}
                  onChange={(zoneIds) => setVariant({ zoneIds })}
                />
              </Field>
              <Field label="Rooms">
                <MultiPick
                  selected={variant.roomIds}
                  disabled={!editable}
                  options={(rooms.data?.items ?? []).map((r) => ({ id: r.id, label: r.number, note: takenBy('roomIds', r.id) }))}
                  onChange={(roomIds) => setVariant({ roomIds })}
                />
              </Field>
              <Field label="Own background photo (optional)">
                <TilePhoto
                  url={urlOf(variant.backgroundAssetId)}
                  disabled={!editable}
                  onChange={(a) => {
                    if (a) remember(a);
                    setVariant({ backgroundAssetId: a?.id ?? null });
                  }}
                />
              </Field>
              <Field label="Own welcome text (optional; empty uses the site's)">
                <input value={variant.welcomeHeadline ?? ''} maxLength={80} placeholder={look.headline} disabled={!editable} onChange={(e) => setVariant({ welcomeHeadline: e.target.value })} />
                <input
                  value={variant.welcomeSubline ?? ''}
                  maxLength={200}
                  placeholder={String(b.welcomeSubline ?? '')}
                  disabled={!editable}
                  style={{ marginTop: 8 }}
                  onChange={(e) => setVariant({ welcomeSubline: e.target.value })}
                />
              </Field>
              {editable && (
                <button
                  type="button"
                  className="btn"
                  style={{ marginTop: 14 }}
                  onClick={() => {
                    setVarDraft(variants.filter((v) => v.id !== variant.id));
                    setSel('default');
                  }}
                >
                  <Trash2 size={16} /> Delete this design
                </button>
              )}
            </Block>
          ) : (
            variants.length > 0 && (
              <div className="tip-card" style={{ margin: '4px 0 8px' }}>
                <MapPin size={20} />
                <div>This design shows in every room that has no other design. {variants.map((v) => v.name).join(', ')}: shown in the rooms chosen for them.</div>
              </div>
            )
          )}

          <Block icon={<Sparkles size={17} />} title="Start from a design" sub="Pick one, then adjust anything below.">
            <div className="choice-grid">
              {PRESETS.map((p) => (
                <button
                  key={p.name}
                  type="button"
                  className="choice"
                  disabled={!editable}
                  onClick={() => set({ ...DEFAULTS, ...p.design, tileImageAssetIds: d.tileImageAssetIds, firstFocus: d.firstFocus, buttonOrder: d.buttonOrder, hiddenKeys: d.hiddenKeys })}
                >
                  <span className="choice-label">{p.name}</span>
                  <span className="choice-hint">{p.hint}</span>
                </button>
              ))}
            </div>
          </Block>

          <Block icon={<LayoutTemplate size={17} />} title="Layout" sub="How the menu buttons are arranged on the screen.">
            <div className="choice-grid">
              {LAYOUTS.map((l) => (
                <button key={l.v} type="button" className={`choice designer-layout-choice${d.layout === l.v ? ' on' : ''}`} disabled={!editable} onClick={() => set({ layout: l.v })}>
                  {l.art}
                  <span>
                    <span className="choice-label">{l.label}</span>
                    <span className="choice-hint" style={{ display: 'block' }}>
                      {l.hint}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </Block>

          <Block icon={<SlidersHorizontal size={17} />} title="Buttons" sub="Style, size and shape of the menu buttons.">
            <Field label="Style">
              <Seg value={d.tileStyle} disabled={!editable || d.layout === 'LEFT_MENU'} onChange={(v) => set({ tileStyle: v })} options={[['ICON_LABEL', 'Icon + name'], ['ICON', 'Big icon'], ['PHOTO', 'Photo']]} />
            </Field>
            <Field label="Size">
              <Seg value={d.tileSize} disabled={!editable} onChange={(v) => set({ tileSize: v })} options={[['S', 'Small'], ['M', 'Medium'], ['L', 'Large']]} />
            </Field>
            <Field label="Shape">
              <Seg value={d.tileShape} disabled={!editable} onChange={(v) => set({ tileShape: v })} options={[['SQUARE', 'Square'], ['ROUNDED', 'Rounded'], ['ROUND', 'Round']]} />
            </Field>
            <Field label="Colours">
              <span className="row" style={{ gap: 14, alignItems: 'center' }}>
                <span className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                  <Toggle on={d.tileColor !== null} onChange={(v) => editable && set({ tileColor: v ? '#1E1A3C' : null })} label="Solid buttons" />
                  <span style={{ fontWeight: 600 }}>Solid buttons</span>
                  {d.tileColor !== null && <ColorWell value={d.tileColor} disabled={!editable} onChange={(c) => set({ tileColor: c })} />}
                </span>
                <span className="row" style={{ gap: 8, alignItems: 'center', flexWrap: 'nowrap' }}>
                  <span style={{ fontWeight: 600 }}>Text</span>
                  <ColorWell value={d.textColor} disabled={!editable} onChange={(c) => set({ textColor: c })} />
                </span>
              </span>
              <div className="muted" style={{ fontSize: 12, marginTop: 6 }}>
                Without solid buttons they are see-through glass. The selected button always takes your accent colour.
              </div>
            </Field>
          </Block>

          <Block icon={<GripVertical size={17} />} title="Buttons on the home screen" sub="Drag to arrange, hide the ones this design should not show, and add a photo for the Photo style and Cards. Add buttons on TV Menu.">
            <div className="designer-items">
              {homeItems.map((m, i) => {
                const img = urlOf(d.tileImageAssetIds[m.key]);
                const hidden = d.hiddenKeys.includes(m.key);
                return (
                  <div
                    key={m.key}
                    className={`designer-item${drag === i ? ' dragging' : ''}${hidden ? ' off' : ''}`}
                    draggable={editable}
                    onDragStart={(e) => {
                      setDrag(i);
                      e.dataTransfer.effectAllowed = 'move';
                    }}
                    onDragOver={(e) => {
                      if (drag === null || drag === i) return;
                      e.preventDefault();
                      moveTo(drag, i);
                      setDrag(i);
                    }}
                    onDragEnd={() => setDrag(null)}
                  >
                    <span className="drag-handle" title="Drag to reorder">
                      <GripVertical size={16} />
                    </span>
                    <span className="designer-item-num">{i + 1}</span>
                    <span className="designer-item-icon">
                      <Glyph name={m.icon || m.defaultIcon} size={17} />
                    </span>
                    <span style={{ flex: 1, fontWeight: 600, minWidth: 0 }}>{label(m)}</span>
                    {d.firstFocus === m.key && <span className="badge brand">Selected first</span>}
                    <button
                      type="button"
                      className="icon-btn"
                      disabled={!editable}
                      title={hidden ? 'Show on the home screen' : 'Hide from the home screen'}
                      onClick={() => set({ hiddenKeys: hidden ? d.hiddenKeys.filter((k) => k !== m.key) : [...d.hiddenKeys, m.key] })}
                    >
                      {hidden ? <EyeOff size={15} /> : <Eye size={15} />}
                    </button>
                    <TilePhoto
                      url={img}
                      disabled={!editable}
                      onChange={(a) => {
                        const next = { ...d.tileImageAssetIds };
                        if (a) {
                          next[m.key] = a.id;
                          remember(a);
                        } else delete next[m.key];
                        set({ tileImageAssetIds: next });
                      }}
                    />
                  </div>
                );
              })}
              {homeItems.length === 0 && <div className="muted">No buttons on the home screen. Turn some on in TV Menu.</div>}
            </div>
            <Field label="Selected first">
              <Select value={d.firstFocus ?? ''} disabled={!editable} onChange={(e) => set({ firstFocus: e.target.value || null })} aria-label="Button selected first">
                <option value="">The first button</option>
                {shownItems.map((m) => (
                  <option key={m.key} value={m.key}>
                    {label(m)}
                  </option>
                ))}
              </Select>
            </Field>
          </Block>

          <Block icon={<Palette size={17} />} title="Screen" sub="Where the logo and welcome go, and what else shows.">
            <Field label="Welcome text">
              <Seg value={d.welcome} disabled={!editable} onChange={(v) => set({ welcome: v })} options={[['LEFT', 'Left'], ['CENTER', 'Centre'], ['HIDDEN', 'Hidden']]} />
            </Field>
            <Field label="Logo">
              <Seg value={d.logo} disabled={!editable} onChange={(v) => set({ logo: v })} options={[['LEFT', 'Left'], ['CENTER', 'Centre']]} />
            </Field>
            <Field label={`Photo darkening ${d.shade}%`}>
              <input type="range" min={0} max={100} step={5} value={d.shade} disabled={!editable} onChange={(e) => set({ shade: Number(e.target.value) })} style={{ width: '100%' }} />
              <div className="muted" style={{ fontSize: 12 }}>
                Less darkening shows more of your photo; text stays readable at 60% and above on most photos.
              </div>
            </Field>
            <div className="row" style={{ gap: 28, marginTop: 12 }}>
              {(
                [
                  ['showPromo', 'Offer card (Marketing)'],
                  ['showHint', 'Remote help line'],
                ] as const
              ).map(([k, text]) => (
                <span key={k} className="row" style={{ gap: 10, flexWrap: 'nowrap' }}>
                  <Toggle on={d[k]} onChange={(v) => editable && set({ [k]: v })} label={text} />
                  <span style={{ fontWeight: 600 }}>{text}</span>
                </span>
              ))}
            </div>
          </Block>
        </div>

        <div className="stack brand-side">
          <div className="card">
            <div className="pkg-head">
              <span className="pkg-head-icon">
                <Eye size={22} />
              </span>
              <span style={{ flex: 1 }}>
                <span className="pkg-head-title">Live preview{variant ? `: ${variant.name}` : ''}</span>
                <span className="muted" style={{ display: 'block', fontSize: 13 }}>
                  Drawn with the same sizes the TVs use. Click a button to have it selected first.
                </span>
              </span>
              <span className="badge">TV · 16:9</span>
            </div>
            <HomePreview look={look} design={d} items={shownItems} urlOf={urlOf} onPick={(k) => editable && set({ firstFocus: k })} />
          </div>
        </div>
      </div>
    </>
  );
}

/** Chips with an "add" list; an option another design already uses says which. */
function MultiPick({ selected, options, disabled, onChange }: { selected: string[]; options: { id: string; label: string; note?: string }[]; disabled?: boolean; onChange: (ids: string[]) => void }) {
  const names = new Map(options.map((o) => [o.id, o.label]));
  return (
    <div className="camp-multi">
      {selected.map((id) => (
        <span key={id} className="chip" style={{ color: '#5b21b6', background: '#f1ebff', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {names.get(id) ?? '…'}
          {!disabled && (
            <button type="button" className="chip-x" onClick={() => onChange(selected.filter((x) => x !== id))} aria-label="Remove">
              <X size={11} />
            </button>
          )}
        </span>
      ))}
      <Select value="" disabled={disabled} onChange={(e) => e.target.value && onChange([...selected, e.target.value])} aria-label="Add">
        <option value="">{selected.length ? '+ Add' : 'None'}</option>
        {options
          .filter((o) => !selected.includes(o.id))
          .map((o) => (
            <option key={o.id} value={o.id}>
              {o.label}
              {o.note ? ` (now in ${o.note})` : ''}
            </option>
          ))}
      </Select>
    </div>
  );
}

function Block({ icon, title, sub, children }: { icon: ReactNode; title: string; sub?: string; children: ReactNode }) {
  return (
    <div className="brand-section">
      <span className="brand-section-icon">{icon}</span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="brand-section-title">{title}</div>
        {sub && <div className="muted brand-section-sub">{sub}</div>}
        <div style={{ marginTop: 10 }}>{children}</div>
      </div>
    </div>
  );
}

function Field({ label: text, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ marginTop: 10 }}>
      <label className="brand-label">{text}</label>
      {children}
    </div>
  );
}

function Seg<T extends string>({ value, options, disabled, onChange }: { value: T; options: [T, string][]; disabled?: boolean; onChange: (v: T) => void }) {
  return (
    <div className="tabs" role="radiogroup">
      {options.map(([v, text]) => (
        <button key={v} type="button" role="radio" aria-checked={value === v} className={`tab${value === v ? ' active' : ''}`} disabled={disabled} onClick={() => onChange(v)}>
          {text}
        </button>
      ))}
    </div>
  );
}

function ColorWell({ value, disabled, onChange }: { value: string; disabled?: boolean; onChange: (c: string) => void }) {
  return (
    <label title="Pick a colour" className="color-well" style={{ background: value }}>
      <input type="color" disabled={disabled} value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} />
    </label>
  );
}

function TilePhoto({ url, disabled, onChange }: { url: string | null; disabled: boolean; onChange: (a: { id: string; url: string } | null) => void }) {
  const input = useRef<HTMLInputElement>(null);
  const upload = useMutation({ mutationFn: (f: File) => api.upload<{ id: string; url: string }>('/assets', f), onSuccess: onChange });
  return (
    <span className="row" style={{ gap: 6, flexWrap: 'nowrap', alignItems: 'center' }}>
      <span className="designer-thumb" title={upload.error ? String((upload.error as Error).message) : undefined}>
        {url ? <img src={url} alt="" /> : <ImagePlus size={15} />}
      </span>
      <button type="button" className="icon-btn" disabled={disabled || upload.isPending} onClick={() => input.current?.click()} title={url ? 'Change photo' : 'Add photo'}>
        <Upload size={15} />
      </button>
      {url && (
        <button type="button" className="icon-btn" disabled={disabled} onClick={() => onChange(null)} title="Remove photo">
          <Trash2 size={15} />
        </button>
      )}
      <input
        ref={input}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) upload.mutate(f);
          e.target.value = '';
        }}
      />
    </span>
  );
}

/** The TV home screen at 1280 × 720, scaled to the card. Positions follow renderHome() in the TV app. */
export function HomePreview({ look, design: d, items, urlOf, onPick }: { look: BrandLook; design: Design; items: MenuItem[]; urlOf: (id: string | null | undefined) => string | null; onPick?: (key: string) => void }) {
  const box = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0.4);
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setScale(el.clientWidth / 1280));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(t);
  }, []);
  const logo = look.logoUrl;
  const bg = look.backgroundUrl;
  const accent = look.accent;
  const g = geometry(d);
  const focusIdx = Math.max(0, d.firstFocus ? items.findIndex((m) => m.key === d.firstFocus) : 0);
  const first = g.kind === 'grid' ? 0 : Math.max(0, Math.min(focusIdx - g.visible + 1, items.length - g.visible));
  const shown = items.slice(first, first + g.visible);
  const shade = d.shade / 100;
  const radius = d.tileShape === 'SQUARE' ? 2 : d.tileShape === 'ROUND' ? (g.kind === 'list' ? g.h / 2 : 30) : 16;
  const time = now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: look.hour12, timeZone: look.timezone });
  const date = now.toLocaleDateString([], { weekday: 'short', day: 'numeric', month: 'short', timeZone: look.timezone });
  const welcomeTop = d.layout === 'LEFT_MENU' ? 260 : d.layout === 'BOTTOM_ROW' ? 236 : 170;
  const welcomeStyle: CSSProperties =
    d.welcome === 'CENTER' ? { left: 0, width: 1280, textAlign: 'center', top: d.layout === 'BOTTOM_ROW' ? 236 : 170 } : { left: d.layout === 'LEFT_MENU' ? 460 : 48, top: welcomeTop, width: 760 };

  return (
    <div ref={box} className="tvp-box" style={{ height: 720 * scale }}>
      <div className="tvp" style={{ transform: `scale(${scale})` }}>
        <div className="tvp-fill" style={bg ? { backgroundImage: `url(${bg})`, backgroundSize: 'cover', backgroundPosition: 'center' } : { background: 'linear-gradient(120deg, #1c1640, #3a2a7a)' }} />
        {shade > 0 && (
          <>
            <div className="tvp-fill" style={{ opacity: shade, background: 'linear-gradient(90deg, rgba(13,10,34,.88) 0%, rgba(13,10,34,.55) 38%, rgba(13,10,34,.05) 72%, rgba(13,10,34,0) 100%)' }} />
            <div className="tvp-fill" style={{ opacity: shade, background: 'linear-gradient(180deg, rgba(13,10,34,.65) 0%, rgba(13,10,34,0) 20%, rgba(13,10,34,0) 50%, rgba(13,10,34,.7) 80%, rgba(13,10,34,.95) 100%)' }} />
          </>
        )}
        <div className="tvp-header">
          {logo ? (
            <img src={logo} alt="" style={d.logo === 'CENTER' ? { left: '50%', transform: 'translateX(-50%)' } : undefined} />
          ) : (
            <div className="tvp-sitename" style={d.logo === 'CENTER' ? { left: 0, right: 0, textAlign: 'center' } : undefined}>
              {look.siteName}
            </div>
          )}
          <div className="tvp-info">
            {look.showRoom && <div>Room 1203</div>}
            {(look.showDate || look.showClock) && <div>{[look.showDate && date, look.showClock && time].filter(Boolean).join('  |  ')}</div>}
          </div>
        </div>
        {d.welcome !== 'HIDDEN' && (
          <div className="tvp-welcome" style={{ ...welcomeStyle, color: d.textColor }}>
            <div className="tvp-title">{look.headline}</div>
            <div className="tvp-sub">{look.subline}</div>
          </div>
        )}
        {d.showPromo && (
          <div className="tvp-promo">
            <div style={{ fontSize: 13, fontWeight: 600, color: '#cbbcff' }}>SPECIAL OFFER</div>
            <div style={{ fontSize: 21, fontWeight: 700, marginTop: 4 }}>Your offer card</div>
            <div style={{ fontSize: 14, lineHeight: '20px', opacity: 0.85, marginTop: 4 }}>Shown when a Marketing card is running.</div>
          </div>
        )}
        {shown.map((m, k) => {
          const i = first + k;
          const pos = g.kind === 'list' ? { x: g.x, y: g.y + k * (g.h + g.gap) } : g.kind === 'grid' ? { x: g.x + (k % g.cols) * (g.w + g.gap), y: g.y + Math.floor(k / g.cols) * (g.h + g.gap) } : { x: g.x + k * (g.w + g.gap), y: g.y };
          const on = i === focusIdx;
          const img = urlOf(d.tileImageAssetIds[m.key]);
          const photo = g.kind !== 'list' && (d.tileStyle === 'PHOTO' || (d.layout === 'CARDS' && !!img));
          const icon = (size: number) => <Glyph name={m.icon || m.defaultIcon} size={size} />;
          return (
            <button
              key={m.key}
              type="button"
              className="tvp-tile"
              onClick={() => onPick?.(m.key)}
              title={`${label(m)}: click to select it first`}
              style={{
                left: pos.x,
                top: pos.y,
                width: g.w,
                height: g.h,
                borderRadius: radius,
                color: d.textColor,
                background: on ? accent : (d.tileColor ?? 'rgba(255,255,255,0.12)'),
                borderColor: on ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.25)',
              }}
            >
              {g.kind === 'list' ? (
                <span style={{ display: 'flex', alignItems: 'center', gap: 14, height: '100%', padding: '0 18px', fontSize: g.h > 60 ? 22 : 19 }}>
                  {icon(30)}
                  {label(m)}
                </span>
              ) : photo ? (
                <>
                  {img && <span className="tvp-fill" style={{ backgroundImage: `url(${img})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />}
                  <span className="tvp-fill" style={{ top: '40%', background: 'linear-gradient(180deg, rgba(0,0,0,0), rgba(0,0,0,.75))' }} />
                  {!img && <span style={{ position: 'absolute', left: 0, right: 0, top: '22%', display: 'flex', justifyContent: 'center' }}>{icon(48)}</span>}
                  <span style={{ position: 'absolute', left: 12, right: 12, bottom: 12, textAlign: 'left', fontSize: g.h > 200 ? 24 : 18 }}>{label(m)}</span>
                </>
              ) : d.tileStyle === 'ICON' ? (
                <span className="tvp-stack">
                  {icon(Math.round(Math.min(g.w, g.h) * 0.42))}
                  <span style={{ fontSize: 14, opacity: 0.85, marginTop: 6 }}>{label(m)}</span>
                </span>
              ) : (
                <span className="tvp-stack">
                  {icon(Math.round(Math.min(g.w, g.h) * 0.3))}
                  <span style={{ fontSize: g.h > 150 ? 20 : 18, marginTop: Math.round(g.h * 0.08) }}>{label(m)}</span>
                </span>
              )}
            </button>
          );
        })}
        {d.showHint && (
          <div className="tvp-hint">
            Use your remote to navigate &nbsp;|&nbsp; Press <b style={{ background: accent }}>OK</b> to select
          </div>
        )}
      </div>
    </div>
  );
}
